const DEFAULT_API_BASE = '/api/service';
const DEFAULT_MIME_TYPE = 'image/png';
const PROMPT_ATTR = 'data-thumbai-prompt';
const ACTION_ATTR = 'data-thumbai-action';
const SERVICE_ATTR = 'data-thumbai-service';
const MODAL_COMMAND = 'thumbai:open-modal';
const THUMBAI_STORE_KEY = '__thumbaiStore';
const THUMBAI_ATTR_PREFIX = 'data-thumbai-';

const sanitizeApiBase = (base) => {
  if (typeof base !== 'string') return DEFAULT_API_BASE;
  const trimmed = base.trim();
  if (!trimmed) return DEFAULT_API_BASE;
  return trimmed.replace(/\/+$/, '');
};

const toParamAttr = (id) => `data-thumbai-${id}`;

const isImageComponent = (component) => {
  if (!component) return false;
  if (typeof component.is === 'function' && component.is('image')) return true;
  const type = component.get && component.get('type');
  const tagName = component.get && component.get('tagName');
  return type === 'image' || tagName === 'img';
};

const pickFirstString = (...values) => {
  for (const value of values) {
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed) return trimmed;
    }
  }
  return '';
};

const formatParamLabel = (template, idx) => {
  if (typeof template !== 'string' || !template) return '';
  if (template.includes('{index}')) {
    return template.replace('{index}', String(idx + 1));
  }
  return template;
};

const getFallbackParamLabel = (param, idx) => {
  if (param && typeof param.id === 'string' && param.id) {
    return param.id;
  }
  return `param${idx + 1}`;
};

const resolveParamLabel = (options, param, idx) => {
  const template = options?.labels?.paramDefaultLabel;
  const labelFromTemplate = formatParamLabel(template, idx);
  if (labelFromTemplate) return labelFromTemplate;
  return getFallbackParamLabel(param, idx);
};

const normalizeParams = (params = [], options) => params.map((param, idx) => ({
  id: param.id || `param${idx + 1}`,
  label: param.label || resolveParamLabel(options, param, idx),
  placeholder: param.placeholder || '',
  required: typeof param.required === 'boolean' ? param.required : true,
}));

const getAttributes = (component) => {
  if (!component) return {};
  if (typeof component.getAttributes === 'function') return component.getAttributes();
  return component.get && component.get('attributes') ? component.get('attributes') : {};
};

const isThumbAttrName = (name) => typeof name === 'string' && name.startsWith(THUMBAI_ATTR_PREFIX);

const normalizeStoreValue = (value) => {
  if (typeof value === 'string') return value;
  if (value === null || typeof value === 'undefined') return '';
  return String(value);
};

const getThumbaiStore = (component) => {
  if (!component || typeof component.get !== 'function') return {};
  const store = component.get(THUMBAI_STORE_KEY);
  return store && typeof store === 'object' ? store : {};
};

const setThumbaiStore = (component, data) => {
  if (!component || typeof component.set !== 'function') return;
  component.set(THUMBAI_STORE_KEY, data);
};

const updateThumbaiStore = (component, updates = {}) => {
  if (!component) return {};
  const current = getThumbaiStore(component);
  if (!Object.keys(updates).length) return current;
  const next = { ...current, ...updates };
  setThumbaiStore(component, next);
  return next;
};

const stripThumbAttributes = (component, attrNames) => {
  if (!component) return;
  const attrs = getAttributes(component);
  if (!attrs || typeof attrs !== 'object') return;
  const keys = Array.isArray(attrNames) && attrNames.length
    ? attrNames.filter((name) => name in attrs)
    : Object.keys(attrs).filter(isThumbAttrName);
  if (!keys.length) return;
  const next = { ...attrs };
  keys.forEach((name) => delete next[name]);
  if (typeof component.setAttributes === 'function') {
    component.setAttributes(next);
  } else if (typeof component.set === 'function') {
    component.set('attributes', next);
  }
};

const setThumbAttributes = (component, attrs = {}) => {
  if (!component || !attrs) return;
  const normalized = {};
  Object.keys(attrs).forEach((key) => {
    normalized[key] = normalizeStoreValue(attrs[key]);
  });
  if (!Object.keys(normalized).length) return;
  updateThumbaiStore(component, normalized);
  const keys = Object.keys(normalized).filter(isThumbAttrName);
  if (keys.length) {
    stripThumbAttributes(component, keys);
  }
};

const migrateThumbAttributes = (component) => {
  if (!component) return;
  const attrs = getAttributes(component);
  if (!attrs || typeof attrs !== 'object') return;
  const entries = {};
  Object.keys(attrs).forEach((name) => {
    if (isThumbAttrName(name)) {
      entries[name] = normalizeStoreValue(attrs[name]);
    }
  });
  if (!Object.keys(entries).length) return;
  updateThumbaiStore(component, entries);
  stripThumbAttributes(component, Object.keys(entries));
};

const getAttrValue = (component, attr) => {
  if (!component || !attr) return '';
  const store = getThumbaiStore(component);
  if (Object.prototype.hasOwnProperty.call(store, attr)) {
    return normalizeStoreValue(store[attr]);
  }

  const attrs = getAttributes(component) || {};
  if (Object.prototype.hasOwnProperty.call(attrs, attr)) {
    const value = normalizeStoreValue(attrs[attr]);
    if (isThumbAttrName(attr)) {
      updateThumbaiStore(component, { [attr]: value });
      stripThumbAttributes(component, [attr]);
    }
    return value;
  }

  return '';
};

const MODAL_STYLE_ID = 'gjs-thumbai-styles';
let modalStylesInjected = false;
const ensureStylesInjected = () => {
  if (modalStylesInjected || typeof document === 'undefined') return;
  const style = document.createElement('style');
  style.id = MODAL_STYLE_ID;
  style.innerHTML = `
    .gjs-thumbai-modal {
      background: #0f172a;
      color: #f8fafc;
      border-radius: 14px;
      padding: 24px;
      min-width: min(760px, calc(100vw - 60px));
      box-shadow: 0 20px 60px rgba(15, 23, 42, 0.55);
      font-family: inherit;
    }
    .gjs-thumbai-modal * {
      box-sizing: border-box;
    }
    .gjs-thumbai-modal__hero {
      margin-bottom: 20px;
    }
    .gjs-thumbai-modal__hero h3 {
      margin: 0 0 4px;
      font-size: 20px;
      font-weight: 600;
      color: #fff;
    }
    .gjs-thumbai-modal__hero p {
      margin: 0;
      color: #cbd5f5;
      font-size: 14px;
    }
    .gjs-thumbai-modal__loading {
      padding: 24px;
      text-align: center;
      font-size: 15px;
      color: #e2e8f0;
    }
    .gjs-thumbai-form {
      display: flex;
      flex-direction: column;
      gap: 18px;
    }
    .gjs-thumbai-form__grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
      gap: 16px;
    }
    .gjs-thumbai-form__section {
      background: #111f35;
      border: 1px solid rgba(148, 163, 184, 0.2);
      border-radius: 12px;
      padding: 16px;
      display: flex;
      flex-direction: column;
      gap: 14px;
    }
    .gjs-thumbai-form__heading {
      font-size: 12px;
      text-transform: uppercase;
      letter-spacing: 0.08em;
      color: #94a3b8;
    }
    .gjs-thumbai-field {
      display: flex;
      flex-direction: column;
      gap: 6px;
      font-size: 13px;
    }
    .gjs-thumbai-field__label {
      color: #e2e8f0;
      font-weight: 600;
    }
    .gjs-thumbai-field__input,
    .gjs-thumbai-field__select {
      width: 100%;
      border-radius: 10px;
      border: 1px solid rgba(148, 163, 184, 0.35);
      background: rgba(15, 23, 42, 0.65);
      color: #f8fafc;
      padding: 10px 12px;
      font-size: 14px;
      font-family: inherit;
      transition: border-color 0.2s ease, box-shadow 0.2s ease;
    }
    .gjs-thumbai-field__input:focus,
    .gjs-thumbai-field__select:focus {
      outline: none;
      border-color: #38bdf8;
      box-shadow: 0 0 0 3px rgba(56, 189, 248, 0.25);
    }
    .gjs-thumbai-field__note {
      color: #94a3b8;
      font-size: 12px;
    }
    .gjs-thumbai-field textarea {
      min-height: 96px;
      resize: vertical;
    }
    .gjs-thumbai-field--wide {
      grid-column: 1 / -1;
    }
    .gjs-thumbai-form__params {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
      gap: 14px;
    }
    .gjs-thumbai-param {
      background: rgba(15, 23, 42, 0.35);
      border-radius: 10px;
      padding: 12px;
      border: 1px solid rgba(148, 163, 184, 0.3);
    }
    .gjs-thumbai-form__empty {
      color: #94a3b8;
      font-size: 13px;
    }
    .gjs-thumbai-form__actions {
      display: flex;
      flex-wrap: wrap;
      gap: 12px;
      align-items: center;
    }
    .gjs-thumbai-form__actions--single {
      justify-content: flex-end;
    }
    .gjs-thumbai-form__hint {
      color: #94a3b8;
      font-size: 12px;
    }
    .gjs-thumbai-modal__status {
      font-size: 13px;
      min-height: 20px;
      color: #cbd5f5;
    }
    .gjs-thumbai-modal__status[data-state="success"] {
      color: #4ade80;
    }
    .gjs-thumbai-modal__status[data-state="error"] {
      color: #f87171;
    }
    .gjs-thumbai-modal__status[data-state="loading"] {
      color: #facc15;
    }
    @media (max-width: 640px) {
      .gjs-thumbai-modal {
        padding: 18px;
        min-width: auto;
      }
      .gjs-thumbai-form__grid {
        grid-template-columns: 1fr;
      }
    }
  `;
  document.head.appendChild(style);
  modalStylesInjected = true;
};

const getServiceList = (options = {}) => (Array.isArray(options.services) ? options.services : []);

const getDefaultServiceSlug = (options = {}) => options.serviceSlug || '';

const resolveServiceSlug = (component, options) =>
  getAttrValue(component, SERVICE_ATTR) || getDefaultServiceSlug(options);

const ensureServiceAttribute = (component, options) => {
  if (!component || !isImageComponent(component)) return;
  const slug = getAttrValue(component, SERVICE_ATTR);
  const defaultSlug = getDefaultServiceSlug(options);
  if (!slug && defaultSlug) {
    setThumbAttributes(component, { [SERVICE_ATTR]: defaultSlug });
  }
};

const toDataUrl = (value, mimeType) => {
  if (typeof value !== 'string') return '';
  const trimmed = value.trim();
  if (!trimmed) return '';
  if (trimmed.startsWith('data:')) return trimmed;
  const sanitized = trimmed.replace(/\s+/g, '');
  if (!sanitized) return '';
  const type = typeof mimeType === 'string' && mimeType.trim() ? mimeType.trim() : DEFAULT_MIME_TYPE;
  return `data:${type};base64,${sanitized}`;
};

const sanitizeBase64 = (value) => (typeof value === 'string' ? value.replace(/\s+/g, '') : '');

const BASE64_REGEX = /^[A-Za-z0-9+/=_-]+$/;

const looksLikeBase64 = (value) => {
  if (typeof value !== 'string') return false;
  const sanitized = sanitizeBase64(value);
  if (!sanitized) return false;
  if (sanitized.startsWith('data:')) return false;
  if (sanitized.length < 32) return false;
  if (sanitized.length % 4 !== 0) return false;
  return BASE64_REGEX.test(sanitized);
};

const getPayloadMimeType = (payload, fallback = DEFAULT_MIME_TYPE) => pickFirstString(
  payload?.mime_type,
  payload?.mimeType,
  payload?.data?.mime_type,
  payload?.data?.mimeType,
  payload?.result?.mime_type,
  payload?.result?.mimeType,
  payload?.info?.mime_type,
  payload?.info?.mimeType,
  payload?.response?.mime_type,
  payload?.response?.mimeType,
  fallback
);

const DATA_URL_REGEX = /^data:([^;,]+)?;base64,(.*)$/i;

const isDataUrl = (value) => typeof value === 'string' && DATA_URL_REGEX.test(value.trim());

const getDataUrlDetails = (value) => {
  if (!isDataUrl(value)) return null;
  const match = value.trim().match(DATA_URL_REGEX);
  if (!match) return null;
  return {
    mimeType: pickFirstString(match[1], DEFAULT_MIME_TYPE),
    base64: sanitizeBase64(match[2] || ''),
  };
};

const arrayBufferToBase64 = (buffer) => {
  if (!buffer) return '';
  const bytes = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
  const chunkSize = 0x8000;
  let binary = '';
  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.subarray(i, i + chunkSize);
    binary += String.fromCharCode.apply(null, chunk);
  }
  return btoa(binary);
};

const fetchAsDataUrl = async (url, fallbackMime = DEFAULT_MIME_TYPE) => {
  if (!url) return '';
  try {
    const response = await fetch(url);
    if (!response || !response.ok) return '';
    const contentType = response.headers ? response.headers.get('content-type') : null;
    const mimeType = pickFirstString(contentType, fallbackMime, DEFAULT_MIME_TYPE);
    const buffer = await response.arrayBuffer();
    const base64 = arrayBufferToBase64(buffer);
    if (!base64) return '';
    return toDataUrl(base64, mimeType);
  } catch (error) {
    return '';
  }
};

const ensureImageSrc = (value, { mimeType, payload, fallbackMime } = {}) => {
  if (typeof value !== 'string') return '';
  const trimmed = value.trim();
  if (!trimmed) return '';

  if (
    /^(?:data:|https?:|blob:|file:|ftp:|ipfs:|cid:)/i.test(trimmed) ||
    /^\/\//.test(trimmed) ||
    /^[./]/.test(trimmed)
  ) {
    return trimmed;
  }

  if (looksLikeBase64(trimmed)) {
    const type = pickFirstString(mimeType, getPayloadMimeType(payload), fallbackMime, DEFAULT_MIME_TYPE);
    return toDataUrl(trimmed, type);
  }

  return trimmed;
};

const normalizeHandlerResult = (result, payload) => {
  if (!result) return '';
  if (typeof result === 'string') return result;
  if (result && typeof result === 'object') {
    const srcCandidate = pickFirstString(
      result.src,
      result.url,
      result.href,
      result.path,
      result.dataUrl,
      result.data_url,
      result.cdn_url,
      result.file_url,
      result.imageUrl,
      result.image_url
    );
    if (srcCandidate) return srcCandidate;

    const base64Candidate = pickFirstString(
      result.base64,
      result.base64Image,
      result.base64_image,
      result.imageBase64,
      result.image_base64
    );
    if (base64Candidate) {
      const type = pickFirstString(result.mimeType, result.mime_type, getPayloadMimeType(payload));
      return toDataUrl(base64Candidate, type);
    }
  }
  return '';
};

const extractImageUrl = (payload, depth = 0) => {
  if (!payload || typeof payload !== 'object' || depth > 3) return '';
  if (typeof payload.data_url === 'string') return payload.data_url;
  if (typeof payload.dataUrl === 'string') return payload.dataUrl;
  if (typeof payload.cdn_url === 'string') return payload.cdn_url;
  if (typeof payload.file_url === 'string') return payload.file_url;
  if (typeof payload.url === 'string') return payload.url;
  if (typeof payload.image_url === 'string') return payload.image_url;
  if (typeof payload.imageUrl === 'string') return payload.imageUrl;
  if (Array.isArray(payload.image_urls) && payload.image_urls.length) return payload.image_urls[0];
  if (Array.isArray(payload.imageUrls) && payload.imageUrls.length) return payload.imageUrls[0];
  if (Array.isArray(payload.urls) && payload.urls.length) return payload.urls[0];
  if (Array.isArray(payload.images) && payload.images.length) {
    const first = payload.images[0];
    if (typeof first === 'string') return first;
    if (first && typeof first === 'object') return first.url || first.cdn_url || first.secure_url || '';
  }
  const base64Value =
    payload.image_base64 ||
    payload.imageBase64 ||
    payload.base64 ||
    payload.base64Image ||
    payload.image_data ||
    payload.imageData;
  if (typeof base64Value === 'string') {
    const mimeType = payload.mime_type || payload.mimeType || 'image/png';
    return toDataUrl(base64Value, mimeType);
  }
  if (payload.data) return extractImageUrl(payload.data, depth + 1);
  if (payload.result) return extractImageUrl(payload.result, depth + 1);
  if (payload.output) return extractImageUrl(payload.output, depth + 1);
  if (payload.response) return extractImageUrl(payload.response, depth + 1);
  if (Array.isArray(payload) && payload.length) return extractImageUrl(payload[0], depth + 1);
  return '';
};

const openSettingsModal = async (editor, component, service, options, state) => {
  const modal = editor.Modal;
  if (!modal) return;

  ensureStylesInjected();

  const focusComponent = () => {
    if (editor && typeof editor.select === 'function') {
      editor.select(component);
    }
    const viewEl = component && component.view && component.view.el;
    if (viewEl && typeof viewEl.focus === 'function') {
      viewEl.focus({ preventScroll: true });
    }
  };

  const closeModal = () => {
    if (modal && typeof modal.close === 'function') {
      modal.close();
    }
  };

  const container = document.createElement('div');
  container.className = 'gjs-thumbai-modal';

  const loading = document.createElement('div');
  loading.className = 'gjs-thumbai-modal__loading';
  loading.textContent = options.labels?.modalLoading || options.labels?.loading || '';
  container.appendChild(loading);

  modal.setTitle(options.labels?.modalTitle || options.labels?.modalHeading || options.labels?.section || '');
  modal.setContent(container);
  modal.open();

  const currentSlug = resolveServiceSlug(component, options);
  let definition = null;
  let activeParams = [];
  let currentServiceSlug = currentSlug;

  if (currentSlug) {
    try {
      definition = await service.getDefinition(currentSlug, { force: true });
      activeParams = definition?.params || state.defaultParams || [];
      await service.prefillComponent(component, currentServiceSlug);
    } catch (error) {
      // If the failure was a 404, close the modal immediately
      if (error?.isNotFound) {
        closeModal();
        return;
      }
      // otherwise continue with defaults
      definition = null;
      activeParams = state.defaultParams || [];
    }
  }

  container.innerHTML = '';

  const heroTitle = options.labels?.modalHeading || options.labels?.modalTitle || options.labels?.section || '';
  const heroSubtitle = options.labels?.modalSubheading || options.labels?.selectService || '';

  const hero = document.createElement('div');
  hero.className = 'gjs-thumbai-modal__hero';
  const heroContent = [];
  if (heroTitle) {
    heroContent.push(`<h3>${heroTitle}</h3>`);
  }
  if (heroSubtitle) {
    heroContent.push(`<p>${heroSubtitle}</p>`);
  }
  hero.innerHTML = heroContent.join('');
  container.appendChild(hero);

  const form = document.createElement('form');
  form.className = 'gjs-thumbai-form';
  form.autocomplete = 'off';

  const metaSection = document.createElement('div');
  metaSection.className = 'gjs-thumbai-form__section';
  const metaGrid = document.createElement('div');
  metaGrid.className = 'gjs-thumbai-form__grid';
  metaSection.appendChild(metaGrid);

  const { field: serviceField, select: serviceSelect } = buildServiceSelect(options, currentServiceSlug);
  metaGrid.appendChild(serviceField);

  form.appendChild(metaSection);

  const paramsSection = document.createElement('div');
  paramsSection.className = 'gjs-thumbai-form__section';
  const paramsHeading = document.createElement('div');
  paramsHeading.className = 'gjs-thumbai-form__heading';
  paramsHeading.textContent = options.labels?.paramsHeading || '';
  paramsSection.appendChild(paramsHeading);

  const paramsContainer = document.createElement('div');
  paramsContainer.className = 'gjs-thumbai-form__params';
  paramsSection.appendChild(paramsContainer);
  form.appendChild(paramsSection);

  const renderParams = (paramList, { forcePrefill = false, slug } = {}) => {
    activeParams = paramList || [];
    paramsContainer.innerHTML = '';
    if (!activeParams.length) {
      const empty = document.createElement('p');
      empty.className = 'gjs-thumbai-form__empty';
      const emptyMessage = slug
        ? options.labels?.noParams || ''
        : options.labels?.selectService || '';
      empty.textContent = emptyMessage;
      paramsContainer.appendChild(empty);
      return;
    }

    activeParams.forEach((param) => {
      const attrName = toParamAttr(param.id);
      const currentValueRaw = getAttrValue(component, attrName);
      const currentValue = typeof currentValueRaw === 'string' ? currentValueRaw : '';
    const placeholderText = pickFirstString(param.placeholder, param.prefillValue, '');

      if (forcePrefill && currentValue) {
        setThumbAttributes(component, { [attrName]: '' });
      }

      const valueToUse = forcePrefill ? '' : currentValue;

      const field = createField({
        label: param.label,
        name: attrName,
        value: valueToUse,
        placeholder: placeholderText,
      });
      field.classList.add('gjs-thumbai-param');
      paramsContainer.appendChild(field);
    });
  };

  renderParams(activeParams, { slug: currentServiceSlug });

  const actions = document.createElement('div');
  actions.className = 'gjs-thumbai-form__actions gjs-thumbai-form__actions--single';

  const generateButton = document.createElement('button');
  generateButton.type = 'button';
  generateButton.className = 'gjs-btn-prim gjs-thumbai-generate';
  generateButton.textContent = options.labels?.generateAction || options.labels?.button || '';

  actions.appendChild(generateButton);
  form.appendChild(actions);

  const status = document.createElement('div');
  status.className = 'gjs-thumbai-modal__status';

  container.appendChild(form);
  container.appendChild(status);

  const setStatus = (message, stateName = '') => {
    status.textContent = message || '';
    status.dataset.state = stateName;
  };

  const applyValues = () => {
    const formData = new FormData(form);
    const attrs = {};
    const selectedService = formData.get(SERVICE_ATTR) || currentServiceSlug;
    currentServiceSlug = selectedService;
    attrs[SERVICE_ATTR] = selectedService;

    (activeParams || []).forEach((param) => {
      const key = toParamAttr(param.id);
      const val = formData.get(key);
      attrs[key] = typeof val === 'string' ? val.trim() : '';
    });

    setThumbAttributes(component, attrs);
    return attrs;
  };

  generateButton.addEventListener('click', async (event) => {
    event.preventDefault();
    generateButton.disabled = true;
  setStatus(options.labels?.loading || '', 'loading');
    applyValues();
    try {
      const imageUrl = await service.generateImage(component);
      component.addAttributes({ src: imageUrl });
  setStatus(options.labels?.success || '', 'success');
      closeModal();
      focusComponent();
    } catch (error) {
      // Close modal immediately on 404-type errors
      if (error?.isNotFound) {
        closeModal();
        return;
      }
      setStatus(error?.message || options.labels?.error || '', 'error');
    } finally {
      generateButton.disabled = false;
    }
  });

  serviceSelect.addEventListener('change', async (event) => {
    const selectedSlug = event.target.value;
    if (!selectedSlug || selectedSlug === currentServiceSlug) return;
  setStatus(options.labels?.modalLoading || options.labels?.loading || '', 'loading');
    serviceSelect.disabled = true;
    generateButton.disabled = true;
    setThumbAttributes(component, { [SERVICE_ATTR]: selectedSlug });

    try {
      let definitionForSlug = null;
      try {
        definitionForSlug = await service.getDefinition(selectedSlug, { force: true });
      } catch (error) {
        if (error?.isNotFound) {
          // Close modal immediately on 404-type errors
          closeModal();
          return;
        }
        // rethrow to outer catch
        throw error;
      }

      const nextParams = definitionForSlug?.params || state.defaultParams;
      await service.prefillComponent(component, selectedSlug);
      renderParams(nextParams, { forcePrefill: true, slug: selectedSlug });
      currentServiceSlug = selectedSlug;
  setStatus(options.labels?.serviceLoaded || options.labels?.saved || '', 'success');
    } catch (error) {
  setStatus(error?.message || options.labels?.loadServiceError || options.labels?.error || '', 'error');
    } finally {
      serviceSelect.disabled = false;
      generateButton.disabled = false;
    }
  });
};

const createService = (editor, options, state) => {
  const prefilledComponents = new WeakMap();
  const infoCache = new Map();
  const pending = new Map();
  const apiBase = sanitizeApiBase(options.apiBase || DEFAULT_API_BASE);
  const prefersBase64 = Boolean(options.base64Image);
  const uploadHandler = typeof options.uploadImage === 'function' ? options.uploadImage : null;

  const ensureCredentials = () => {
    if (!options.apiToken || !options.userId) {
      const message = options.labels?.missingCredentials || '';
      throw new Error(message);
    }
  };

  const buildParamList = (data = {}, fallback = []) => {
  const normalizedFallback = normalizeParams(fallback, options);
    const pickDemoRequest = (payload = {}) => {
      const candidates = [
        payload.demo_request,
        payload.template?.demo_request,
        payload.info?.demo_request,
        payload.result?.demo_request,
        payload.service?.demo_request,
      ];

      for (const entry of candidates) {
        if (!entry) continue;
        if (entry.params && typeof entry.params === 'object') {
          return entry.params;
        }
        if (typeof entry === 'object') {
          return entry;
        }
      }

      return {};
    };

    const pickFields = (payload = {}) => {
      const candidates = [
        payload.fields,
        payload.param_fields,
        payload.template?.fields,
        payload.info?.fields,
        payload.result?.fields,
        payload.service?.fields,
        payload.demo_request?.fields,
      ];
      return candidates.find((entry) => Array.isArray(entry) && entry.length) || null;
    };

    const demo = pickDemoRequest(data);
    const mapFieldEntries = (entries = []) =>
      entries.map((entry, idx) => {
        const fallbackEntry = normalizedFallback[idx] || {};
        const fallbackId = fallbackEntry.id || `param${idx + 1}`;
        const id = entry.param || entry.id || fallbackEntry.id || fallbackId;
        const label = pickFirstString(
          entry.label,
          entry.name,
          entry.title,
          fallbackEntry.label,
          resolveParamLabel(options, { id }, idx),
          id
        );
        const primaryPlaceholder = pickFirstString(
          Array.isArray(entry.examples) ? entry.examples[0] : '',
          entry.example
        );
        const secondaryPlaceholder = pickFirstString(
          entry.placeholder,
          entry.placeholder_text,
          entry.placeholderText,
          entry.sample,
          entry.sample_value,
          entry.hint,
          entry.help,
          demo[id],
          fallbackEntry.placeholder,
          fallbackEntry.prefillValue
        );
        const placeholder = primaryPlaceholder || secondaryPlaceholder;
        const prefillValue = pickFirstString(
          entry.prefill,
          entry.prefill_value,
          entry.default,
          entry.default_value,
          entry.value,
          entry.token,
          entry.template_value,
          demo[id],
          placeholder,
          fallbackEntry.prefillValue,
          fallbackEntry.placeholder
        );
        const required =
          typeof entry.required === 'boolean'
            ? entry.required
            : typeof fallbackEntry.required === 'boolean'
              ? fallbackEntry.required
              : true;

        return {
          id,
          label,
          placeholder,
          prefillValue,
          required,
        };
      });

    const explicitFields = pickFields(data);
    if (explicitFields) {
      return mapFieldEntries(explicitFields);
    }

    if (Array.isArray(data.params) && data.params.length) {
  return mapFieldEntries(normalizeParams(data.params, options));
    }

    const count = Number.isInteger(data.param_count) ? data.param_count : normalizedFallback.length;
    if (!count) return mapFieldEntries(normalizedFallback);

    return mapFieldEntries(
      Array.from({ length: count }).map((_, idx) => {
        const defaults = normalizedFallback[idx] || {};
        return {
          ...defaults,
          id: defaults.id || `param${idx + 1}`,
        };
      })
    );
  };

  const fetchServiceInfo = async (slug) => {
    if (!slug) {
      const message = options.labels?.missingService || '';
      throw new Error(message);
    }
    ensureCredentials();
    const query = new URLSearchParams({
      service: slug,
      api_token: options.apiToken,
      user_id: options.userId,
    });

  const response = await fetch(`${apiBase}/info?${query.toString()}`);
    if (response && response.status === 404) {
      if (typeof window !== 'undefined' && typeof window.alert === 'function') {
        window.alert(
          options.labels?.notFoundError ||
            'Service not found (404). Please check your apiToken, user_id, or clientDomain/domain.'
        );
      }
      const err = new Error(options.labels?.serviceInfoError || options.labels?.error || '');
      err.isNotFound = true;
      throw err;
    }
    if (!response.ok) {
      throw new Error(options.labels?.serviceInfoError || options.labels?.error || '');
    }

    const raw = await response.json();
    const payload = raw && typeof raw === 'object' && raw.data ? raw.data : raw;
    const params = buildParamList(payload, state.defaultParams);
    const definition = { info: payload, params };
    infoCache.set(slug, definition);
    return definition;
  };

  const queueFetch = (slug) => {
    if (!slug) {
      const message = options.labels?.missingService || '';
      return Promise.reject(new Error(message));
    }
    const request = fetchServiceInfo(slug)
      .then((definition) => {
        pending.delete(slug);
        return definition;
      })
      .catch((error) => {
        pending.delete(slug);
        throw error;
      });
    pending.set(slug, request);
    return request;
  };

  const getDefinition = async (slug, { force = false } = {}) => {
    if (!slug) {
      const message = options.labels?.missingService || '';
      throw new Error(message);
    }
    if (!force && infoCache.has(slug)) {
      return infoCache.get(slug);
    }

    if (!force && pending.has(slug)) {
      return pending.get(slug);
    }

    return queueFetch(slug);
  };

  const collectParams = (component, params = []) => {
    const values = {};
    params.forEach((param) => {
      const attrName = toParamAttr(param.id);
      const rawValue = getAttrValue(component, attrName);
      values[param.id] = typeof rawValue === 'string' ? rawValue.trim() : rawValue;
    });
    return values;
  };

  const generateImage = async (component) => {
    ensureCredentials();
    const slug = resolveServiceSlug(component, options);
    if (!slug) {
      throw new Error(options.labels?.missingService || '');
    }
    const { info, params } = await getDefinition(slug);
    if (!info || !info.prompt_id) {
      throw new Error(options.labels?.missingPromptId || '');
    }

    const paramValues = collectParams(component, params);
    const missing = params.filter((param) => param.required !== false && !paramValues[param.id]);
    if (missing.length) {
      const missingLabel = missing.map((entry) => entry.label || entry.id).join(', ');
      const missingMessage = options.labels?.missingParams || '';
      throw new Error(missingMessage ? `${missingMessage} (${missingLabel})` : missingLabel);
    }

    const query = new URLSearchParams({
      api_token: options.apiToken,
      user_id: options.userId,
      prompt_id: info.prompt_id,
    });

    params.forEach((param) => {
      if (paramValues[param.id]) {
        query.set(param.id, paramValues[param.id]);
      }
    });

    const headers = options.clientDomain ? { 'X-Client-Domain': options.clientDomain } : {};
    const response = await fetch(`${apiBase}/generate-image?${query.toString()}`, { headers });
    if (response && response.status === 404) {
      if (typeof window !== 'undefined' && typeof window.alert === 'function') {
        window.alert(
          options.labels?.notFoundError ||
            'Image generation endpoint returned 404. Please check your apiToken, user_id, or clientDomain/domain.'
        );
      }
      const message = await response.text().catch(() => null);
      const err = new Error(message || options.labels?.error || '');
      err.isNotFound = true;
      throw err;
    }
    if (!response.ok) {
      const message = await response.text().catch(() => null);
      throw new Error(message || options.labels?.error || '');
    }

  const payload = await response.json().catch(() => ({}));
  const payloadMimeType = getPayloadMimeType(payload);
  const queryEntries = Object.fromEntries(query.entries());
    let imageUrl = '';

    if (typeof options.onImageResponse === 'function') {
      const handlerResult = await options.onImageResponse(payload, {
        component,
        slug,
        params: paramValues,
        query: queryEntries,
        defaultImage: () => extractImageUrl(payload),
      });
      imageUrl = normalizeHandlerResult(handlerResult, payload);
    }

    if (!imageUrl) {
      imageUrl = extractImageUrl(payload);
    }

    imageUrl = ensureImageSrc(imageUrl, {
      payload,
      mimeType: payloadMimeType,
      fallbackMime: payloadMimeType,
    });

    if (!imageUrl) {
      throw new Error(options.labels?.missingImage || '');
    }

    let dataUrl = isDataUrl(imageUrl) ? imageUrl : '';
    let dataDetails = dataUrl ? getDataUrlDetails(dataUrl) : null;
    const needsDataUrl = prefersBase64 || Boolean(uploadHandler);

    if (needsDataUrl && !dataUrl) {
      const converted = await fetchAsDataUrl(imageUrl, payloadMimeType);
      if (converted) {
        dataUrl = converted;
        dataDetails = getDataUrlDetails(converted);
        if (prefersBase64) {
          imageUrl = converted;
        }
      }
    }

    if (prefersBase64 && !dataUrl) {
      throw new Error(options.labels?.base64ConversionFailed || '');
    }

    let finalImage = prefersBase64 && dataUrl ? dataUrl : imageUrl;

    if (uploadHandler) {
      const uploadContext = {
        component,
        slug,
        params: paramValues,
        query: queryEntries,
        payload,
        imageUrl,
        dataUrl,
        base64: dataDetails?.base64 || '',
    mimeType: dataDetails?.mimeType || payloadMimeType,
        defaultImage: () => (prefersBase64 && dataUrl ? dataUrl : imageUrl),
      };

      try {
        const uploadResult = await uploadHandler(uploadContext);
        const normalizedUpload = normalizeHandlerResult(uploadResult, payload);
        const ensuredUpload = ensureImageSrc(normalizedUpload, {
          payload,
          mimeType: uploadContext.mimeType,
          fallbackMime: uploadContext.mimeType,
        });
        if (ensuredUpload) {
          finalImage = prefersBase64 && dataUrl ? dataUrl : ensuredUpload;
          if (isDataUrl(ensuredUpload)) {
            dataUrl = ensuredUpload;
            dataDetails = getDataUrlDetails(ensuredUpload);
          }
        }
      } catch (error) {
        const uploadMessage = error?.message || options.labels?.uploadError || options.labels?.error || '';
        throw new Error(uploadMessage);
      }
    }

    return finalImage;
  };

  const extractDemoRequest = (payload = {}) => {
    const candidates = [
      payload.demo_request,
      payload.template?.demo_request,
      payload.info?.demo_request,
      payload.result?.demo_request,
      payload.service?.demo_request,
    ];

    for (const entry of candidates) {
      if (!entry || typeof entry !== 'object') continue;
      const params = typeof entry.params === 'object' && entry.params ? entry.params : entry;
      const prompt = typeof entry.prompt === 'string' ? entry.prompt : params?.prompt;
      return {
        prompt,
        params,
      };
    }

    return { prompt: '', params: {} };
  };

  const prefillComponent = (component, slug) => {
    if (!options.autoPrefillDemoRequest || !component) {
      return Promise.resolve();
    }

    const serviceSlug = slug || resolveServiceSlug(component, options);
    if (!serviceSlug) {
      return Promise.resolve();
    }
    const seenForComponent = prefilledComponents.get(component) || new Set();
    if (seenForComponent.has(serviceSlug)) {
      return Promise.resolve();
    }

    return getDefinition(serviceSlug)
      .then(({ info, params }) => {
        const demo = extractDemoRequest(info);
        const attrs = {};
        const currentPrompt = getAttrValue(component, PROMPT_ATTR);
        if (demo.prompt && !currentPrompt) {
          attrs[PROMPT_ATTR] = demo.prompt;
        }

        if (Object.keys(attrs).length) {
          setThumbAttributes(component, attrs);
        }

        seenForComponent.add(serviceSlug);
        prefilledComponents.set(component, seenForComponent);
      })
      .catch(() => {});
  };

  return {
    generateImage,
    prefillComponent,
    getDefinition,
    getDefinitionSync: (slug) => infoCache.get(slug),
  };
};

const registerTraitType = (traitManager, service, options) => {
  if (!traitManager || typeof traitManager.addType !== 'function') return;
  const typeId = 'thumbai-generate';
  if (traitManager.getType && traitManager.getType(typeId)) return;

  traitManager.addType(typeId, {
    events: {
      'click button': 'handleClick',
    },

    createInput() {
      const el = document.createElement('div');
      el.className = 'gjs-thumbai-trait';

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'gjs-btn-prim gjs-thumbai-button';
  button.textContent = options.labels?.traitButton || options.labels?.generateAction || options.labels?.button || '';

      const status = document.createElement('div');
      status.className = 'gjs-thumbai-status';

      el.appendChild(button);
      el.appendChild(status);

      this.button = button;
      this.status = status;

      return el;
    },

    handleClick(event) {
      event.preventDefault();
      if (this.button?.disabled) return;
      this.generateImage();
    },

    async generateImage() {
      const component = this.target;
      if (!component) return;

      const loadingMessage = options.labels?.loading || '';
      this.setState(loadingMessage, 'loading');
      try {
        const imageUrl = await service.generateImage(component);
        component.addAttributes({ src: imageUrl });
        this.setState(options.labels?.traitSuccess || options.labels?.success || '', 'success');
      } catch (error) {
        const fallback = options.labels?.traitError || options.labels?.error || '';
        this.setState(error?.message || fallback, 'error');
      } finally {
        this.clearLoading();
      }
    },

    setState(message, className) {
      if (this.button) {
        this.button.disabled = className === 'loading';
      }
      if (this.status) {
        this.status.textContent = message || '';
        this.status.dataset.state = className || '';
      }
    },

    clearLoading() {
      if (this.button) {
        this.button.disabled = false;
      }
    },
  });
};

const hasTrait = (component, name) => {
  if (!component || !name) return false;
  if (typeof component.getTrait === 'function') {
    return !!component.getTrait(name);
  }

  const traits = component.get && component.get('traits');
  if (!traits) return false;

  if (typeof traits.where === 'function') {
    return traits.where({ name }).length > 0;
  }

  if (Array.isArray(traits)) {
    return traits.some((trait) => trait.name === name);
  }

  return false;
};

const addTraits = (component, traits) => {
  if (!component || !traits.length) return;
  if (typeof component.addTrait === 'function') {
    component.addTrait(traits);
    return;
  }

  const existing = component.get && component.get('traits');
  if (Array.isArray(existing)) {
    component.set('traits', existing.concat(traits));
  }
};

const collectTraitModels = (component) => {
  if (!component) return [];
  const traits = component.get && component.get('traits');
  if (!traits) return [];
  if (typeof traits.each === 'function') {
    const models = [];
    traits.each((trait) => models.push(trait));
    return models;
  }
  if (Array.isArray(traits)) return traits;
  if (Array.isArray(traits.models)) return traits.models;
  return [];
};

const getTraitName = (trait) => {
  if (!trait) return '';
  if (typeof trait.get === 'function') {
    return trait.get('name') || '';
  }
  return trait.name || trait.id || '';
};

const RESERVED_TRAIT_NAMES = new Set([SERVICE_ATTR, PROMPT_ATTR, ACTION_ATTR]);

const isThumbParamTrait = (name) => {
  if (!name || typeof name !== 'string') return false;
  if (RESERVED_TRAIT_NAMES.has(name)) return false;
  return name.startsWith('data-thumbai-');
};

const syncParamTraits = (component, params = []) => {
  if (!component) return;
  const desired = new Set(
    (params || []).map((param) => toParamAttr(param.id)).filter((name) => typeof name === 'string' && name)
  );

  collectTraitModels(component).forEach((trait) => {
    const name = getTraitName(trait);
    if (isThumbParamTrait(name) && !desired.has(name) && typeof component.removeTrait === 'function') {
      component.removeTrait(name);
    }
  });

  (params || []).forEach((param) => {
    const traitName = toParamAttr(param.id);
    const traitLabel = param.label;
  const traitPlaceholder = pickFirstString(param.placeholder, param.prefillValue, '');
    const existingTrait = typeof component.getTrait === 'function' ? component.getTrait(traitName) : null;

    if (existingTrait && typeof existingTrait.set === 'function') {
      if (traitLabel) {
        existingTrait.set('label', traitLabel);
      }
      existingTrait.set('placeholder', traitPlaceholder);
      if (typeof existingTrait.trigger === 'function') {
        existingTrait.trigger('change', existingTrait);
      }
      return;
    }

    if (typeof component.addTrait === 'function') {
      component.addTrait({
        type: 'text',
        name: traitName,
        label: traitLabel,
        placeholder: traitPlaceholder,
        changeProp: 1,
      });
    }
  });
};

const ensureToolbarButton = (component, commandId, options) => {
  if (!component || !isImageComponent(component) || !commandId) return;
  const currentToolbar = component.get && component.get('toolbar');
  const toolbar = Array.isArray(currentToolbar) ? currentToolbar.slice() : [];
  const existingIndex = toolbar.findIndex((item) => item && item.command === commandId);
  const button = existingIndex >= 0
    ? toolbar.splice(existingIndex, 1)[0]
    : {
        attributes: {
          class: 'gjs-thumbai-toolbar-btn',
          title: options.labels?.toolbarTitle || options.labels?.toolbarLabel || options.labels?.section || '',
        },
        label:
          `<svg fill="#000000" version="1.1" id="Capa_1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 48 48" xml:space="preserve"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"> <g> <g> <path d="M47.987,21.938c-0.006-0.091-0.023-0.178-0.053-0.264c-0.011-0.032-0.019-0.063-0.033-0.094 c-0.048-0.104-0.109-0.202-0.193-0.285c-0.001-0.001-0.001-0.001-0.001-0.001L42,15.586V10c0-0.022-0.011-0.041-0.013-0.063 c-0.006-0.088-0.023-0.173-0.051-0.257c-0.011-0.032-0.019-0.063-0.034-0.094c-0.049-0.106-0.11-0.207-0.196-0.293l-9-9 c-0.086-0.086-0.188-0.148-0.294-0.197c-0.03-0.014-0.059-0.022-0.09-0.032c-0.086-0.03-0.172-0.047-0.263-0.052 C32.039,0.011,32.021,0,32,0H7C6.448,0,6,0.448,6,1v14.586l-5.707,5.707c0,0-0.001,0.001-0.002,0.002 c-0.084,0.084-0.144,0.182-0.192,0.285c-0.014,0.031-0.022,0.062-0.033,0.094c-0.03,0.086-0.048,0.173-0.053,0.264 C0.011,21.96,0,21.978,0,22v19c0,0.552,0.448,1,1,1h5v5c0,0.552,0.448,1,1,1h34c0.552,0,1-0.448,1-1v-5h5c0.552,0,1-0.448,1-1V22 C48,21.978,47.989,21.96,47.987,21.938z M44.586,21H42v-2.586L44.586,21z M38.586,9H33V3.414L38.586,9z M8,2h23v8 c0,0.552,0.448,1,1,1h8v5v5H8v-5V2z M6,18.414V21H3.414L6,18.414z M40,46H8v-4h32V46z M46,40H2V23h5h34h5V40z"></path> <path d="M20.481,25.964L17.489,38h2.04l0.612-2.55h3.162L23.898,38h2.125l-2.941-12.036H20.481z M20.396,33.852l1.309-5.967h0.068 l1.275,5.967H20.396z"></path> <rect x="27.281" y="25.964" width="2.04" height="12.036"></rect> </g> </g> </g></svg>`,
        command: commandId,
      };

  toolbar.unshift(button);
  component.set('toolbar', toolbar);
};

const createField = ({ label, name, value, placeholder, multiline, note, variant }) => {
  const wrapper = document.createElement('label');
  wrapper.className = 'gjs-thumbai-field';
  if (variant) {
    wrapper.classList.add(`gjs-thumbai-field--${variant}`);
  }

  const heading = document.createElement('span');
  heading.className = 'gjs-thumbai-field__label';
  heading.textContent = label;

  const input = multiline ? document.createElement('textarea') : document.createElement('input');
  if (!multiline) {
    input.type = 'text';
  }
  input.className = 'gjs-thumbai-field__input';
  input.name = name;
  input.value = value || '';
  if (placeholder) {
    input.placeholder = placeholder;
  }

  wrapper.appendChild(heading);
  wrapper.appendChild(input);

  return wrapper;
};

const buildServiceSelect = (options, currentSlug) => {
  const wrapper = document.createElement('label');
  wrapper.className = 'gjs-thumbai-field';

  const heading = document.createElement('span');
  heading.className = 'gjs-thumbai-field__label';
  heading.textContent = options.labels?.service || '';

  const select = document.createElement('select');
  select.className = 'gjs-thumbai-field__select';
  select.name = SERVICE_ATTR;

  const placeholderOption = document.createElement('option');
  placeholderOption.value = '';
  placeholderOption.textContent = options.labels?.servicePlaceholder || '';
  placeholderOption.disabled = true;

  if (!currentSlug) {
    placeholderOption.selected = true;
  }

  select.appendChild(placeholderOption);

  getServiceList(options).forEach((entry) => {
    const option = document.createElement('option');
    option.value = entry.slug;
    option.textContent = entry.label;
    if (entry.slug === currentSlug) {
      option.selected = true;
    }
    select.appendChild(option);
  });

  wrapper.appendChild(heading);
  wrapper.appendChild(select);

  return { field: wrapper, select };
};

const registerModalCommand = (editor, service, options, state) => {
  if (!editor.Commands) return null;
  if (editor.Commands.get && editor.Commands.get(MODAL_COMMAND)) return MODAL_COMMAND;

  let detachModalWatcher = null;
  let lastSender = null;

  const watchModalClose = (ed, callback) => {
    const modal = ed.Modal;
    if (!modal || typeof modal.getModel !== 'function') return null;
    const model = modal.getModel();
    if (!model || typeof model.on !== 'function' || typeof model.off !== 'function') return null;

    const handleChange = () => {
      const isOpen = typeof modal.isOpen === 'function'
        ? modal.isOpen()
        : typeof model.get === 'function'
          ? model.get('open')
          : false;
      if (!isOpen) {
        callback();
      }
    };

    model.on('change:open', handleChange);
    return () => model.off('change:open', handleChange);
  };

  const deactivateSender = (sender) => {
    if (sender && typeof sender.set === 'function') {
      sender.set('active', false);
    }
  };

  editor.Commands.add(MODAL_COMMAND, {
    run(ed, sender, opts = {}) {
      const component = opts.component || (typeof opts.getComponent === 'function' ? opts.getComponent() : null) || ed.getSelected && ed.getSelected();
      lastSender = sender;

      if (!component || !isImageComponent(component)) {
        deactivateSender(sender);
        return;
      }

      if (detachModalWatcher) {
        detachModalWatcher();
        detachModalWatcher = null;
      }

      detachModalWatcher = watchModalClose(ed, () => {
        if (detachModalWatcher) {
          detachModalWatcher();
          detachModalWatcher = null;
        }

        if (ed.Commands && typeof ed.Commands.isActive === 'function' && !ed.Commands.isActive(MODAL_COMMAND)) {
          return;
        }

        ed.stopCommand(MODAL_COMMAND, lastSender || sender || null);
      });

      openSettingsModal(ed, component, service, options, state);
    },
    stop(ed, sender) {
      if (detachModalWatcher) {
        detachModalWatcher();
        detachModalWatcher = null;
      }
      if (ed.Modal) {
        ed.Modal.close();
      }
      deactivateSender(sender || lastSender);
      if (sender && sender === lastSender) {
        lastSender = null;
      }
    },
  });

  return MODAL_COMMAND;
};

const ensureTraits = (component, service, options) => {
  if (!component || !isImageComponent(component)) return;
  migrateThumbAttributes(component);
  ensureServiceAttribute(component, options);
  const slug = resolveServiceSlug(component, options);
  if (slug) {
    service.prefillComponent(component, slug).catch(() => {});
  }
};

export default (editor, opts = {}) => {
  const state = {
    defaultParams: normalizeParams(opts.params || [], opts),
  };

  const service = createService(editor, opts, state);
  registerTraitType(editor.TraitManager, service, opts);
  const commandId = registerModalCommand(editor, service, opts, state);

  const handleComponent = (component) => {
    ensureTraits(component, service, opts);
    ensureToolbarButton(component, commandId, opts);
  };

  editor.on('component:selected', handleComponent);
  editor.on('component:add', handleComponent);
  editor.on('load', () => {
    const wrapper = editor.getWrapper && editor.getWrapper();
    if (!wrapper || typeof wrapper.find !== 'function') return;
    wrapper.find('image').forEach(handleComponent);
  });

  // Ensure currently selected component (if any) gets the traits immediately
  const selected = editor.getSelected && editor.getSelected();
  if (selected) handleComponent(selected);
};

